import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

const FORM_ENDPOINT = "https://formspree.io/f/xaqrqjrj";

const initialForm = {
  name: "",
  email: "",
  company: "",
  companyAbout: "",
  contentGoals: "",
};

const OnboardingModal = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please complete name and email.");
      return;
    }
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    if (!emailOk) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        FORM_ENDPOINT,
        {
          formType: "Subscription Onboarding",
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim() || "",
          companyAbout: form.companyAbout.trim() || "",
          contentGoals: form.contentGoals.trim() || "",
          _subject: "Subscription onboarding request — weaver.video",
        },
        { headers: { Accept: "application/json" } }
      );
      toast.success("Received. A team member will contact you within one business day.");
      setForm(initialForm);
      onOpenChange(false);
    } catch (err) {
      const fsErrors = err?.response?.data?.errors;
      let msg = "Something went wrong. Please try again.";
      if (Array.isArray(fsErrors) && fsErrors[0]?.message) {
        msg = fsErrors[0].message;
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="onboarding-modal"
        className="bg-[#0A0A0A] border-white/10 text-white max-w-xl sm:rounded-none p-0 max-h-[90vh] overflow-y-auto"
      >
        <div className="p-8 sm:p-10">
          <DialogHeader>
            <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
              — Subscription Onboarding
            </span>
            <DialogTitle
              className="mt-3 text-3xl sm:text-4xl font-black uppercase tracking-tight leading-none"
              data-testid="onboarding-modal-title"
            >
              Just need a little
              <br />
              <span className="text-zinc-500">info about yourself.</span>
            </DialogTitle>
            <DialogDescription className="mt-4 text-sm font-light text-zinc-400 leading-relaxed">
              Once we give it a readover we can begin the onboarding. A team
              member will contact you within one business day to outline the
              short process, then we can start on your first project! We
              prorate the first month, then invoice you monthly thereafter,
              with no commitment to continue. Cancel at any time.
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={handleSubmit}
            noValidate
            className="mt-8 space-y-5"
            data-testid="onboarding-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label
                  className="block text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 mb-2"
                  htmlFor="onboarding-name"
                >
                  Name *
                </label>
                <input
                  id="onboarding-name"
                  type="text"
                  required
                  value={form.name}
                  onChange={handleChange("name")}
                  data-testid="onboarding-input-name"
                  className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-base font-light text-white placeholder-zinc-600 focus:outline-none focus:border-white transition-colors"
                  placeholder="Your name"
                  autoComplete="name"
                />
              </div>
              <div>
                <label
                  className="block text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 mb-2"
                  htmlFor="onboarding-email"
                >
                  Email *
                </label>
                <input
                  id="onboarding-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange("email")}
                  data-testid="onboarding-input-email"
                  className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-base font-light text-white placeholder-zinc-600 focus:outline-none focus:border-white transition-colors"
                  placeholder="you@brand.com"
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <label
                className="block text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 mb-2"
                htmlFor="onboarding-company"
              >
                Company
              </label>
              <input
                id="onboarding-company"
                type="text"
                value={form.company}
                onChange={handleChange("company")}
                data-testid="onboarding-input-company"
                className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-base font-light text-white placeholder-zinc-600 focus:outline-none focus:border-white transition-colors"
                placeholder="Your Company Name"
                autoComplete="organization"
              />
            </div>

            <div>
              <label
                className="block text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 mb-2"
                htmlFor="onboarding-company-about"
              >
                What does your company do?
              </label>
              <textarea
                id="onboarding-company-about"
                rows={2}
                value={form.companyAbout}
                onChange={handleChange("companyAbout")}
                data-testid="onboarding-input-company-about"
                className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-base font-light text-white placeholder-zinc-600 focus:outline-none focus:border-white transition-colors resize-none"
                placeholder="A sentence or two is plenty..."
              />
            </div>

            <div>
              <label
                className="block text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 mb-2"
                htmlFor="onboarding-content-goals"
              >
                What sort of content do you want to make?
              </label>
              <textarea
                id="onboarding-content-goals"
                rows={3}
                value={form.contentGoals}
                onChange={handleChange("contentGoals")}
                data-testid="onboarding-input-content-goals"
                className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-base font-light text-white placeholder-zinc-600 focus:outline-none focus:border-white transition-colors resize-none"
                placeholder="Social videos, brand films, animation, podcasts..."
              />
            </div>

            <div className="pt-4 flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                data-testid="onboarding-submit-btn"
                className="group inline-flex items-center gap-3 px-9 py-4 bg-white text-black font-bold text-xs uppercase tracking-[0.25em] hover:bg-zinc-200 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Sending
                  </>
                ) : (
                  <>
                    Submit
                    <ArrowRight
                      size={16}
                      className="transition-transform duration-500 group-hover:translate-x-1"
                    />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingModal;
